"use client";

import { useToast } from "@/components/feedback";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAccessToken } from "@/hooks";
import { ClassEnrollmentsApi } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";

export function useClassRegistration(studentId: number) {
  const accessToken = useAccessToken();
  const toast = useToast();
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);

  const toggleClassSelection = useCallback((id: number) => {
    setSelectedClasses((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const { mutate: submitEnrollment, isPending: isSubmitting } = useMutation({
    mutationFn: async ({
      studentId,
      classIds,
    }: {
      studentId: number;
      classIds: number[];
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
      return service.apiClassEnrollmentsBulkPost({
        createBulkEnrollmentRequest: {
          studentId,
          classIds,
        },
      });
    },
    onSuccess: (data) => {
      const createdCount = data.created?.length ?? 0;
      const failedCount = data.failed?.length ?? 0;

      if (data.created && data.created.length > 0) {
        const createdClassIds = data.created.map((c) => c.classId);
        setSelectedClasses((prev) =>
          prev.filter((id) => !createdClassIds.includes(id)),
        );
      }

      if (failedCount === 0) {
        toast({
          duration: 5000,
          isClosable: true,
          status: "success",
          position: "top-right",
          title: "Registration successful",
          description: `Successfully enrolled in ${createdCount} class(es)!`,
        });
      } else {
        const errorMessages =
          data.failed
            ?.map((f) => {
              const error = f.error as any;
              const msg = error?.message || error?.code || "Unknown error";
              return `Class ID ${f.classId}: ${msg}`;
            })
            .join("\n") || "All enrollments failed.";

        toast({
          duration: 8000,
          isClosable: true,
          status: failedCount === data?.failed?.length ? "error" : "warning",
          position: "top-right",
          title:
            failedCount === data?.failed?.length
              ? "Registration failed"
              : "Partial success",
          description: errorMessages,
        });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit registration";
      
      toast({
        duration: 5000,
        status: "error",
        isClosable: true,
        position: "top-right",
        title: "Registration failed",
        description: errorMessage,
      });
    },
  });

  const submitRegistration = () => {
    if (selectedClasses.length === 0) {
      toast({
        duration: 3000,
        status: "warning",
        position: "top-right",
        title: "No classes selected",
        description: "Please select at least one class.",
      });
      return;
    }

    submitEnrollment({ studentId, classIds: selectedClasses });
  };

  return {
    selectedClasses,
    toggleClassSelection,
    submitRegistration,
    isSubmitting,
  };
}
